import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Defense-in-depth auth: the backend prefers the HttpOnly `session_token`
// cookie, but cookies fail on some cross-origin / third-party-cookie-blocked
// deployments (Safari ITP, Chrome 2024+ on cross-site setups). To keep the
// app working across all browser/deployment matrices we ALSO persist the
// session token to localStorage and attach it as an `Authorization: Bearer`
// header on every request. `core/deps._resolve_user_from_request` already
// falls back to the bearer header when no cookie is present.
const SESSION_KEY = "v_session_token";
export const setSessionToken = (token) => {
  try {
    if (token) localStorage.setItem(SESSION_KEY, token);
    else localStorage.removeItem(SESSION_KEY);
  } catch { /* localStorage unavailable (private mode etc.) */ }
};
export const getSessionToken = () => {
  try { return localStorage.getItem(SESSION_KEY) || ""; } catch { return ""; }
};
export const clearSessionToken = () => setSessionToken(null);

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const t = getSessionToken();
  if (t && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

export const AuthAPI = {
  exchange: (session_id) => api.post("/auth/session", { session_id }),
  me: () => api.get("/auth/me"),
  myCredits: () => api.get("/auth/me/credits"),
  logout: () => api.post("/auth/logout"),
  setTheme: (theme) => api.post("/auth/theme", { theme }),
  superAdminLogin: (password) => api.post("/auth/super_admin_login", { password }),
  superAdminPassword: (current_password, new_password) => api.post("/auth/super_admin_password", { current_password, new_password }),
};

export const BillingAPI = {
  bundles: () => api.get("/billing/bundles"),
  wallet: () => api.get("/billing/wallet"),
  checkout: (bundle_id) => api.post("/billing/checkout", { bundle_id }),
  verify: (payload) => api.post("/billing/verify", payload),
  adminBundles: () => api.get("/admin/billing/bundles"),
  adminUpdateBundle: (bid, body) => api.put(`/admin/billing/bundles/${bid}`, body),
  adminCreateBundle: (body) => api.post("/admin/billing/bundles", body),
  adminDeleteBundle: (bid) => api.delete(`/admin/billing/bundles/${bid}`),
  adminRevenue: () => api.get("/admin/billing/revenue"),
};

export const ProjectsAPI = {
  list: (q) => api.get("/projects", { params: q ? { q } : {} }),
  create: (data) => api.post("/projects", data),
  get: (id) => api.get(`/projects/${id}`),
  patch: (id, data) => api.patch(`/projects/${id}`, data),
  assessments: (id) => api.get(`/projects/${id}/assessments`),
};

export const AssessmentsAPI = {
  create: (data) => api.post("/assessments", data),
  get: (id, opts) => api.get(`/assessments/${id}`, { params: opts || {} }),
  emailReport: (data) => api.post("/email/report", data),
  exportUrl: (id, format) => `${API}/assessments/${id}/export?format=${format}`,
};

export const VariationSetsAPI = {
  list: (projectId) => api.get(`/projects/${projectId}/variation-sets`),
  create: (projectId, data) => api.post(`/projects/${projectId}/variation-sets`, data),
  get: (id) => api.get(`/variation-sets/${id}`),
  patch: (id, data) => api.patch(`/variation-sets/${id}`, data),
  remove: (id) => api.delete(`/variation-sets/${id}`),
  imageUrl: (id, variationId, idx = 0) => `${API}/variation-sets/${id}/image?variation_id=${variationId}&idx=${idx}`,
  exportUrl: (id, format = "pdf") => `${API}/variation-sets/${id}/export?format=${format}`,
};

export const AssetsAPI = {
  list: (projectId, kind) => api.get(`/projects/${projectId}/assets`, { params: kind ? { kind } : {} }),
  get: (projectId, assetId) => api.get(`/projects/${projectId}/assets/${assetId}`),
  upload: (projectId, items) => api.post(`/projects/${projectId}/assets`, { items }),
  fromUrl: (projectId, data) => api.post(`/projects/${projectId}/assets/from_url`, data),
  patch: (projectId, assetId, data) => api.patch(`/projects/${projectId}/assets/${assetId}`, data),
  remove: (projectId, assetId) => api.delete(`/projects/${projectId}/assets/${assetId}`),
  downloadUrl: (projectId, assetId) => `${API}/projects/${projectId}/assets/${assetId}?download=1`,
  bulkFetch: (projectId, asset_ids) => api.post(`/projects/${projectId}/assets/bulk_fetch`, { asset_ids }),
};

export const CollaboratorsAPI = {
  list: (projectId) => api.get(`/projects/${projectId}/collaborators`),
  add: (projectId, email) => api.post(`/projects/${projectId}/collaborators`, { email }),
  remove: (projectId, userId) => api.delete(`/projects/${projectId}/collaborators/${userId}`),
};

export const CommentsAPI = {
  list: (assessmentId) => api.get(`/assessments/${assessmentId}/comments`),
  add: (assessmentId, body, dimension_key) => api.post(`/assessments/${assessmentId}/comments`, { body, dimension_key }),
  remove: (assessmentId, commentId) => api.delete(`/assessments/${assessmentId}/comments/${commentId}`),
};

export const ProfileAPI = {
  patch: (data) => api.patch("/auth/profile", data),
};

export const AdminAPI = {
  analytics: () => api.get("/admin/analytics"),
  analyticsOverview: (days = 30) => api.get("/admin/analytics/overview", { params: { days } }),
  assessments: (q) => api.get("/admin/assessments", { params: q ? { q } : {} }),
  designers: () => api.get("/admin/designers"),
  designersCsvUrl: () => `${API}/admin/designers/export.csv`,
  assessmentsCsvUrl: () => `${API}/admin/assessments/export.csv`,
  deleteAssessment: (id) => api.delete(`/admin/assessments/${id}`),
  deleteProject: (id) => api.delete(`/admin/projects/${id}`),
  patchUser: (id, data) => api.patch(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  allowlist: () => api.get("/admin/allowlist"),
  addAllowlist: (email, note) => api.post("/admin/allowlist", { email, note }),
  removeAllowlist: (email) => api.delete(`/admin/allowlist/${encodeURIComponent(email)}`),
  approvals: (status = "pending") => api.get("/admin/approvals", { params: { status } }),
  approve: (id, addToAllowlist = true) => api.post(`/admin/approvals/${id}/approve`, { add_to_allowlist: addToAllowlist }),
  reject: (id) => api.post(`/admin/approvals/${id}/reject`),
  rejectAndDelete: (id) => api.post(`/admin/approvals/${id}/reject_and_delete`),
  getPrompts: () => api.get("/admin/prompts"),
  patchPrompts: (payload) => api.patch("/admin/prompts", payload),
  resetPrompts: (fields) => api.post("/admin/prompts/reset", { fields }),
  setDesignerCap: (userId, cap) => api.patch(`/admin/designers/${userId}/credit-cap`, { cap }),
  resetDesignerUsage: (userId) => api.post(`/admin/designers/${userId}/credit-reset`),
  sendTestEmail: (userId) => api.post(`/admin/designers/${userId}/send_test_email`),
  factoryReset: () => api.post("/admin/factory_reset", { confirm: "FACTORY_RESET" }),
  jobsSummary: () => api.get("/admin/jobs/summary"),
  jobsEmails: (status, limit = 100) => api.get("/admin/jobs/emails", { params: { status, limit } }),
  jobsRetryEmail: (jobId) => api.post(`/admin/jobs/emails/${jobId}/retry`),
  jobsAudits: () => api.get("/admin/jobs/audits"),
};
